import { useState, useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useApps } from '@/hooks/useApps'
import {
  useBulkJobs, useBulkJobItems, useBulkValuate, useEnqueueBulkGenerate,
  useBulkWorkerNow, useCancelBulkJob, type BulkKind, type BulkJob,
} from '@/hooks/useBulkJobs'
import { SectionHeader, Card, CardTitle, CardDescription, Button, Badge, Skeleton, Empty } from '@/components/ui'
import { Boxes, Sparkles, Briefcase, Play, X, RefreshCw, Calculator, ChevronRight, Activity, CheckCircle2, AlertCircle, Cloud } from 'lucide-react'
import { cn, formatRelativeDate } from '@/lib/utils'

const KINDS: { value: BulkKind; label: string; helper: string; cost: string }[] = [
  { value: 'valuation',          label: 'Valuation',        helper: '5-band Gemini valuation, ~30s/app',   cost: 'cheap' },
  { value: 'valuation_doc',      label: 'Branded Doc',      helper: 'Google Doc into Drive, ~20s/app',      cost: 'free' },
  { value: 'whitepaper',         label: 'Whitepaper',       helper: 'Gemini + Gamma, ~90s/app',            cost: 'gamma' },
  { value: 'marketing_deck',     label: 'Marketing Deck',   helper: 'Gemini + Gamma, ~120s/app',           cost: 'gamma' },
  { value: 'improvement_audit',  label: 'Value Audit',      helper: '6-12 improvement items + prompts, ~30s/app', cost: 'cheap' },
]

export function OperationsPage() {
  const { data: apps } = useApps()
  const { active, recent, loading } = useBulkJobs()
  const [selectedKinds, setSelectedKinds] = useState<BulkKind[]>(['valuation', 'valuation_doc', 'improvement_audit'])
  const [force, setForce] = useState(false)
  const [openJob, setOpenJob] = useState<string | null>(null)

  const bulkValuate = useBulkValuate()
  const enqueueGen = useEnqueueBulkGenerate()
  const workerNow = useBulkWorkerNow()

  const counts = useMemo(() => {
    if (!apps) return { unvaluated: 0, missingDoc: 0, missingWp: 0, missingDeck: 0, total: 0 }
    return {
      unvaluated:  apps.filter(a => !a.valuation).length,
      missingDoc:  apps.filter(a => !a.valuation_doc?.google_doc_id).length,
      missingWp:   apps.filter(a => !a.whitepaper?.url).length,
      missingDeck: apps.filter(a => !a.marketing_deck?.gamma_url).length,
      total: apps.length,
    }
  }, [apps])

  const queuedCount = useMemo(() => {
    if (!apps) return 0
    let n = 0
    for (const a of apps) {
      for (const k of selectedKinds) {
        if (force) { n += 1; continue }
        if (k === 'valuation' && !a.valuation) n += 1
        else if (k === 'valuation_doc' && !a.valuation_doc?.google_doc_id) n += 1
        else if (k === 'whitepaper' && !a.whitepaper?.url) n += 1
        else if (k === 'marketing_deck' && !a.marketing_deck?.gamma_url) n += 1
        else if (k === 'improvement_audit' && !(a.improvement_audit?.iteration ?? 0)) n += 1
      }
    }
    return n
  }, [apps, selectedKinds, force])

  return (
    <div className="space-y-8">
      <SectionHeader
        eyebrow="Bulk Operations"
        title="Generate everything, in batch"
        description="Run valuations, branded Google Docs, whitepapers, and marketing decks across the whole portfolio. Live progress · cancellable · idempotent."
      />

      <Card className="border-success/25 bg-success/5">
        <div className="flex items-start gap-3">
          <Cloud size={18} className="text-success shrink-0 mt-0.5" />
          <div>
            <div className="font-heading text-base text-cream mb-1">Close the laptop, work continues.</div>
            <p className="font-body text-sm text-cream/80 leading-relaxed">
              Every operation here runs server-side on Cloud Functions. The
              scheduled <span className="text-success">bulkWorker</span> fires every minute on Google's
              infra and the long-running <span className="text-success">bulkValuate</span> persists to
              Firestore as it goes. You can disconnect, sleep the machine, walk away — when you
              come back, refresh this page and the active jobs reflect current state.
              The only thing tied to your browser is the success-toast notification.
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-cream/45 mt-2">
              Bulk Valuate vs Bulk Generate (valuation kind) overlap — they do the same work. Pick one.
              Bulk Valuate is faster (~6 min); Bulk Generate's queue covers the other kinds too.
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPI eyebrow="Unvaluated" value={counts.unvaluated} total={counts.total} tone="gold" />
        <KPI eyebrow="No branded doc" value={counts.missingDoc} total={counts.total} tone="bronze" />
        <KPI eyebrow="No whitepaper" value={counts.missingWp} total={counts.total} tone="info" />
        <KPI eyebrow="No marketing deck" value={counts.missingDeck} total={counts.total} tone="warning" />
      </div>

      <Card>
        <CardTitle>Bulk valuate</CardTitle>
        <CardDescription className="mt-1">
          Single long-running call (concurrency 3, ~6 min for 38 apps). Skips apps that already have a valuation unless <em>force</em> is on.
        </CardDescription>
        <div className="flex items-center gap-3 mt-4">
          <Button
            onClick={() => bulkValuate.mutate(force)}
            loading={bulkValuate.isPending}
            disabled={counts.unvaluated === 0 && !force}
          >
            <Calculator size={14} />
            {force ? `Re-valuate ALL ${counts.total} apps` : `Valuate ${counts.unvaluated} unvaluated`}
          </Button>
          <ForceToggle force={force} onChange={setForce} />
        </div>
      </Card>

      <Card>
        <CardTitle>Bulk generate (queued)</CardTitle>
        <CardDescription className="mt-1">
          Enqueues a job, returns immediately, processed by a 1-minute scheduled worker (batch of 8, ~8 min/batch).
          Starts with low-cost valuation, branded docs, and value audits selected. Whitepaper + marketing deck jobs consume Gamma credits, so select them intentionally when you need polished buyer artifacts.
        </CardDescription>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5 mb-4">
          {KINDS.map(k => {
            const on = selectedKinds.includes(k.value)
            return (
              <button
                key={k.value}
                onClick={() => setSelectedKinds(s => on ? s.filter(x => x !== k.value) : [...s, k.value])}
                className={cn(
                  'rounded-md border p-3 text-left transition-all',
                  on ? 'bg-gold/12 border-gold/40 text-cream' : 'bg-navy/30 border-cream/15 text-cream/70 hover:border-cream/30',
                )}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-heading text-sm">{k.label}</span>
                  {on && <CheckCircle2 size={12} className="text-gold-soft" />}
                </div>
                <div className="font-mono text-[10px] text-cream/55 mb-1">{k.helper}</div>
                <Badge tone={k.cost === 'gamma' ? 'warning' : k.cost === 'cheap' ? 'success' : 'cream'} className="!py-0">
                  {k.cost === 'gamma' ? 'Gamma credit' : k.cost === 'cheap' ? 'Gemini only' : 'Free'}
                </Badge>
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={() => enqueueGen.mutate({ kinds: selectedKinds, force })}
            loading={enqueueGen.isPending}
            disabled={queuedCount === 0 || selectedKinds.length === 0}
          >
            <Sparkles size={14} />
            {force
              ? `Re-generate ALL ${queuedCount} items`
              : `Queue ${queuedCount} items`}
          </Button>
          <ForceToggle force={force} onChange={setForce} />
          <Button variant="ghost" size="sm" loading={workerNow.isPending} onClick={() => workerNow.mutate()}>
            <RefreshCw size={12} /> Run worker now
          </Button>
        </div>
      </Card>

      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="eyebrow mb-1">Active jobs</div>
            <h2 className="font-heading text-xl text-cream">{active.length} running</h2>
          </div>
        </div>
        {loading ? <Skeleton className="h-32" /> : active.length === 0 ? (
          <Empty icon={<Activity size={36} />} title="No active jobs" description="Queue something above and it'll show up here in real time." />
        ) : (
          <div className="space-y-3">
            {active.map(j => <JobCard key={j.id} job={j} expanded={openJob === j.id} onToggle={() => setOpenJob(openJob === j.id ? null : j.id)} />)}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="eyebrow mb-1">Recent</div>
            <h2 className="font-heading text-xl text-cream">{recent.length} jobs</h2>
          </div>
        </div>
        {recent.length === 0 ? null : (
          <div className="space-y-2">
            {recent.slice(0, 10).map(j => <JobCard key={j.id} job={j} expanded={openJob === j.id} onToggle={() => setOpenJob(openJob === j.id ? null : j.id)} compact />)}
          </div>
        )}
      </section>
    </div>
  )
}

function KPI({ eyebrow, value, total, tone }: { eyebrow: string; value: number; total: number; tone: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  const color = tone === 'gold' ? 'text-gold-soft' : tone === 'bronze' ? 'text-bronze' : tone === 'info' ? 'text-info' : 'text-warning'
  return (
    <div className="glass rounded-lg p-5">
      <div className={cn('eyebrow mb-2', color)}>{eyebrow}</div>
      <div className="font-display italic text-4xl text-cream font-light leading-none mb-1">{value}</div>
      <div className="font-mono text-[10px] text-cream/45">of {total} · {pct}%</div>
    </div>
  )
}

function ForceToggle({ force, onChange }: { force: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className={cn('flex items-center gap-2 cursor-pointer', force ? 'text-warning' : 'text-cream/55')}>
      <input type="checkbox" checked={force} onChange={(e) => onChange(e.target.checked)} className="accent-warning" />
      <span className="font-mono text-[10px] uppercase tracking-[0.12em]">Force re-run on already-done</span>
    </label>
  )
}

function JobCard({ job, expanded, onToggle, compact = false }: { job: BulkJob; expanded: boolean; onToggle: () => void; compact?: boolean }) {
  const cancel = useCancelBulkJob()
  const items = useBulkJobItems(expanded ? job.id : null)
  const pct = job.total > 0 ? Math.round(((job.succeeded + job.failed) / job.total) * 100) : 0
  const tone = job.status === 'completed' ? 'success' : job.status === 'cancelled' ? 'cream' : job.status === 'failed' ? 'danger' : 'gold'
  const colorMap = { success: 'text-success border-success/30', cream: 'text-cream border-cream/30', danger: 'text-danger border-danger/30', gold: 'text-gold-soft border-gold/30' }

  return (
    <Card className={cn('!p-4 transition-all border', expanded ? 'border-gold/40' : 'border-cream/10', compact && '!p-3')}>
      <div className="w-full text-left">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button onClick={onToggle} className="flex items-center gap-3 min-w-0 flex-1 text-left">
            <ChevronRight size={14} className={cn('text-cream/45 transition-transform', expanded && 'rotate-90')} />
            <div className="min-w-0">
              <div className={cn('font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded border inline-block', colorMap[tone])}>
                {job.status}
              </div>
              <span className="font-mono text-[10px] text-cream/45 ml-2">
                {job.type === 'bulk_valuate' ? 'Bulk valuate' : 'Bulk generate'} ·
                kinds: {job.kinds.join(', ')} ·
                {job.created_at ? ` started ${formatRelativeDate((job.created_at.toDate?.() ?? new Date()).toISOString())}` : ''}
              </span>
            </div>
          </button>
          <div className="flex items-center gap-3 shrink-0">
            <span className="font-mono text-xs text-cream">
              <span className="text-success">{job.succeeded}</span> /
              <span className="text-danger ml-1">{job.failed}</span> /
              <span className="text-cream/55 ml-1">{job.total}</span>
            </span>
            {(job.status === 'queued' || job.status === 'running') && (
              <Button size="sm" variant="danger" onClick={() => cancel.mutate(job.id)} loading={cancel.isPending}>
                <X size={11} /> Cancel
              </Button>
            )}
          </div>
        </div>
        <button onClick={onToggle} aria-label={expanded ? 'Collapse job' : 'Expand job'} className="block w-full">
          <div className="h-1 rounded-full bg-cream/8 overflow-hidden mt-3">
            <div className={cn('h-full rounded-full transition-all',
              tone === 'success' ? 'bg-success' : tone === 'danger' ? 'bg-danger' : tone === 'cream' ? 'bg-cream/60' : 'bg-gold-soft')}
              style={{ width: `${pct}%` }} />
          </div>
        </button>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-cream/8">
          {items.length === 0 ? (
            <div className="text-cream/55 text-sm">No items.</div>
          ) : (
            <div className="space-y-1 max-h-96 overflow-auto">
              {[...items].sort((a, b) => a.app_slug.localeCompare(b.app_slug)).map(item => (
                <div key={item.id} className="flex items-center gap-3 py-1.5 px-2 rounded text-xs hover:bg-cream/5">
                  <ItemStatusIcon status={item.status} />
                  <Link to="/apps/$slug" params={{ slug: item.app_slug }} className="text-cream hover:text-gold-soft min-w-0 flex-1 truncate">
                    {item.app_name}
                  </Link>
                  <Badge tone="bronze" className="!py-0 shrink-0">{item.kind}</Badge>
                  {item.error && (
                    <span className="font-mono text-[10px] text-danger truncate max-w-[200px]" title={item.error}>
                      {item.error.slice(0, 60)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

function ItemStatusIcon({ status }: { status: string }) {
  if (status === 'done') return <CheckCircle2 size={12} className="text-success shrink-0" />
  if (status === 'failed') return <AlertCircle size={12} className="text-danger shrink-0" />
  if (status === 'running') return <span className="size-2.5 rounded-full bg-gold-soft animate-pulse shrink-0" />
  if (status === 'cancelled') return <X size={12} className="text-cream/40 shrink-0" />
  return <span className="size-2.5 rounded-full bg-cream/30 shrink-0" />
}

// hush unused
void Boxes; void Briefcase; void Play
